import {Interval} from "luxon";
import {singleton} from "tsyringe";

/**
 * Service for calculating delta intervals when dragging timeline view.
 * Calculates only the newly visible areas (left/right of previous interval)
 * to optimize loading performance with IntervalLoadingGuard.
 */
@singleton()
export class TimelineDragIntervalCalculator {

    /**
     * Calculate only the newly visible intervals when dragging timeline.
     * Returns intervals that need to be loaded (delta between old and new).
     * 
     * @param oldInterval Previously visible interval
     * @param newInterval Currently visible interval  
     * @returns Array of intervals that need to be loaded
     */
    public calculateDragDeltaIntervals(oldInterval: Interval, newInterval: Interval): Interval[] {
        const deltaIntervals: Interval[] = [];

        // New area to the left (dragging right shows earlier dates)
        if (newInterval.start! < oldInterval.start!) {
            const leftDelta = Interval.fromDateTimes(newInterval.start!, oldInterval.start!);
            if (leftDelta.isValid && !leftDelta.isEmpty()) {
                deltaIntervals.push(leftDelta);
            }
        }

        // New area to the right (dragging left shows later dates)
        if (newInterval.end! > oldInterval.end!) {
            const rightDelta = Interval.fromDateTimes(oldInterval.end!, newInterval.end!);
            if (rightDelta.isValid && !rightDelta.isEmpty()) {
                deltaIntervals.push(rightDelta);
            }
        }

        return deltaIntervals;
    }

    /**
     * Check if intervals overlap significantly to determine if delta calculation makes sense.
     * If intervals don't overlap much, it might be more efficient to load the full new interval.
     * 
     * @param oldInterval Previously visible interval
     * @param newInterval Currently visible interval
     * @returns True if delta calculation is beneficial
     */
    public shouldUseDeltaCalculation(oldInterval: Interval, newInterval: Interval): boolean {
        if (!oldInterval.overlaps(newInterval)) {
            // No overlap - delta calculation not beneficial
            return false;
        }

        const intersection = oldInterval.intersection(newInterval);
        if (!intersection) {
            return false;
        }

        // If more than 30% overlap, use delta calculation
        const overlapRatio = intersection.length() / Math.max(oldInterval.length(), newInterval.length());
        return overlapRatio > 0.3;
    }
}